import dayjs, { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import * as yup from "yup";
import { InferType } from "yup";

const inviteValidationSchema = (
  t: TFunction,
  country: string,
  dateOfBirth: string,
  stateIdRequired: string,
  phoneNumberRequired: string,
  isParticipantReleaseDateMandatory: boolean = false,
  coloringModule: boolean = false,
  coloringRequired: boolean = false,
  isEditMode: boolean = false
) => {
  return yup.lazy(() => {
    const dateOfBirthValidation = (): yup.StringSchema<string | undefined> => {
      return dateOfBirth === "mandatory"
        ? yup
            .string()
            .required(t("validations.required"))
            .test(
              "futureDate",
              "Date of birth cannot be in the future",
              (value) => {
                if (!value) return true;
                return dayjs(value).isBefore(dayjs());
              }
            )
        : yup
            .string()
            .optional()
            .test(
              "futureDate",
              "Date of birth cannot be in the future",
              (value) => {
                if (!value) return true;
                return dayjs(value).isBefore(dayjs());
              }
            );
    };

    const localeValidation = (): yup.StringSchema => {
      const base = (() => {
        switch (country) {
          case "US":
            return yup
              .string()
              .test(
                "min",
                t("validations.validator_digits_min", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length >= 10;
                }
              )
              .test(
                "max",
                t("validations.validator_digits_max", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length <= 10;
                }
              )
              .test("phoneNumber", t("validations.invalid_format"), (value) => {
                if (!value || value.trim() === "") return true;
                const phoneRegex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
                return phoneRegex.test(value);
              });
          case "UK":
            return yup
              .string()
              .test(
                "min",
                t("validations.validator_digits_min", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length >= 10;
                }
              )
              .test(
                "max",
                // 11: a UK number written with its leading 0 (PRET-2026).
                t("validations.validator_digits_max", { count: 11 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length <= 11;
                }
              )
              .test("phoneNumber", t("validations.invalid_format"), (value) => {
                if (!value || value.trim() === "") return true;
                const phoneRegex = /^\(?\d{4}\)?[-. ]?\d{6,7}$/;
                return phoneRegex.test(value);
              });
          default:
            return yup.string();
        }
      })();

      return phoneNumberRequired === "mandatory"
        ? base.required(t("validations.required"))
        : base.optional();
    };

    const stateIdValidation = (): yup.StringSchema<string | undefined> => {
      return stateIdRequired === "mandatory"
        ? yup.string().required("This field is required")
        : yup.string().optional();
    };

    const releaseDateSchema = isParticipantReleaseDateMandatory
      ? yup
          .date()
          .nullable()
          .required("This field is require")
          .transform((value, originalValue) => {
            return originalValue === "" ? null : value;
          })
      : yup
          .date()
          .nullable()
          .default(null)
          .transform((value, originalValue) => {
            return originalValue === "" ? null : value;
          });

    const schemaFields: yup.ObjectShape = {
      firstName: yup
        .string()
        .required("This field is required")
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      lastName: yup
        .string()
        .required("This field is required")
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      preferredName: yup
        .string()
        .required("This field is required")
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      email: yup
        .string()
        .required("This field is required")
        .email(t("validations.invalid_format"))
        .max(100, t("validations.max_length", { count: 100 })),
      dateOfBirth: dateOfBirthValidation(),
      phoneNumber: localeValidation(),
      caseNumbers: stateIdValidation(),
      externalId: yup.string().optional(),
      programId: yup.string().required("This field is required"),
      orgUnitIds: yup.string().required("This field is required"),
      colorId:
        coloringModule && coloringRequired
          ? yup.string().required("This field is required")
          : yup.string().optional().nullable(),
      releaseDate: releaseDateSchema,
      // PRET-1837 AC4 -- both halves are required and a partial allocation counts as none, so
      // each field carries its own message naming what is missing rather than a shared one.
      treatmentAgencyId: yup
        .string()
        .required(
          "Select a treatment agency to complete the treatment service assignment"
        ),
      locationId: yup
        .string()
        .required(
          "Select a location to complete the treatment service assignment"
        ),
      // Design screen 5a section 2. The pickers hand back Dayjs objects, so these are mixed()
      // rather than string().
      firstAppointmentDate: yup
        .mixed()
        .required("Record the first appointment date to complete enrolment")
        .test(
          "after-release-date",
          "The first appointment must start after the release date",
          function (value) {
            // The server rejects this too, but only once the coordinator has filled the whole
            // form and pressed save. Catching it here keeps the correction next to the field.
            // Eligibility begins the DAY AFTER release, so the release date itself and anything
            // earlier are both invalid — a participant is still in custody before release.
            const releaseDate = this.parent?.releaseDate;
            if (!releaseDate || !value) return true;
            return dayjs(value as Dayjs)
              .startOf("day")
              .isAfter(dayjs(releaseDate as Date).startOf("day"));
          }
        ),
      firstAppointmentStart: yup
        .mixed()
        .required("Record the first appointment start time"),
      firstAppointmentEnd: yup
        .mixed()
        .required("Record the first appointment end time")
        .test(
          "after-start",
          "The end time must be after the start time",
          function (value) {
            const start = this.parent?.firstAppointmentStart;
            if (!start || !value) return true;
            return dayjs(value as Dayjs).isAfter(dayjs(start as Dayjs));
          }
        ),
      firstAppointmentReasonId: yup
        .string()
        .required("Select a reason for the first appointment"),
    };

    if (isEditMode) {
      // The first appointment already exists when editing, so it is not asked for again.
      for (const field of [
        "firstAppointmentDate",
        "firstAppointmentStart",
        "firstAppointmentEnd",
      ]) {
        schemaFields[field] = yup.mixed().optional();
      }
      schemaFields["firstAppointmentReasonId"] = yup.string().optional();

      // Design 8b renders no personal-information block, so the edit modal no longer shows date
      // of birth or phone number. Both are conditionally REQUIRED by account settings
      // (`dateOfBirth === "mandatory"`, `phoneNumberRequired === "mandatory"`) and a participant
      // can legitimately have neither on record — so leaving them required would reject the save
      // on a field the coordinator cannot see or fill. The symptom is silent: SAVE stays enabled,
      // nothing is sent, nothing is saved.
      //
      // Only the two CONDITIONAL ones are relaxed. firstName, lastName, preferredName and email
      // stay required: they are always populated for an existing participant and travel unchanged
      // in the payload, so the requirement costs nothing and still catches a malformed update.
      schemaFields["dateOfBirth"] = yup.string().optional();
      schemaFields["phoneNumber"] = yup.string().optional();

      // Required, but NOT restricted to an allow-list.
      //
      // This used to be .oneOf(["ACTIVE", "INACTIVE", "PENDING"]). The form seeds status from the
      // participant's real value, and that list names "PENDING" -- not a ParticipantStatus at all
      // -- while omitting PAUSED, COMPLETED, CANCELED, DISENROLLED, PENDING_ACTIVATION,
      // PENDING_TO_ENROLL, PENDING_VERIFICATION, DENIED and the API's INVITED. So editing a
      // participant in any of those states failed validation on a field the coordinator had not
      // touched, and because the Status select offers only Active/Paused -- and is disabled while
      // the participant is PENDING_VERIFICATION -- there was no way to satisfy it from the UI.
      // The symptom was silent: SAVE enabled, no message, no request, nothing saved.
      //
      // What the user can CHOOSE is already constrained by the select's options; an existing
      // status must not block saving unrelated fields.
      //
      // Now OPTIONAL rather than required, because design 8b removes the Status select from the
      // edit modal altogether. Required plus off-screen is the silent-failure combination: a
      // participant whose status the resolver rejects could never be saved and would never be
      // told why. The modal sends the persisted status back unchanged, so nothing is lost by not
      // demanding it here.
      schemaFields["status"] = yup.string().optional();
    }

    return yup.object().shape(schemaFields);
  });
};

export type InviteFormData = InferType<
  ReturnType<typeof inviteValidationSchema>
>;

export default inviteValidationSchema;
